(() => {
  if (window.tudiGallerySwipeReady) return;
  window.tudiGallerySwipeReady = true;
  function init(surface) {
    if (surface.dataset.swipeReady) return;
    surface.dataset.swipeReady = 'true';
    const events = new AbortController();
    const options = { signal: events.signal };
    const controls = 'button, input, select, textarea, label, summary, [contenteditable="true"], [data-no-swipe]';
    const isControl = target => {
      const control = target.closest(controls);
      return control && !control.hasAttribute('data-swipe-handle');
    };
    let gesture;
    let wheel;
    let wheelTimer;
    let suppressClickUntil = 0;
    let surfaceWidth = surface.clientWidth;
    const canDrag = () => [...surface.querySelectorAll('[data-slide], [data-angle-slide]')].filter(slide => !slide.hidden).length > 1;
    function emit(phase, offset = 0, commit = false) {
      surface.dispatchEvent(new CustomEvent('tudi:gallery-drag', {
        bubbles: true, detail: { phase, offset, commit }
      }));
    }
    function reset(notify = false) {
      const previous = gesture;
      const dragging = previous?.locked || Boolean(wheel);
      gesture = undefined;
      wheel = undefined;
      clearTimeout(wheelTimer);
      if (previous?.kind === 'pointer' && surface.hasPointerCapture(previous.id)) surface.releasePointerCapture(previous.id);
      if (dragging) suppressClickUntil = Date.now() + 350;
      if (notify && dragging) emit('cancel');
    }
    const directTouch = 'ontouchstart' in window;
    const threshold = 16;
    function sample(x, time) {
      gesture.samples.push({ x, time });
      gesture.samples = gesture.samples.filter(item => time - item.time <= 100);
    }
    function begin(id, x, y, time, kind) {
      reset(true);
      suppressClickUntil = 0;
      gesture = { id, x, y, kind, locked: false, samples: [{ x, time }] };
    }
    function move(event, x, y) {
      const dx = x - gesture.x;
      const dy = y - gesture.y;
      sample(x, event.timeStamp);
      if (!gesture.locked && Math.abs(dy) > 6 && Math.abs(dy) > Math.abs(dx) * 1.2) {
        reset();
        return;
      }
      if (!gesture.locked && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        gesture.locked = true;
        if (gesture.kind === 'pointer') surface.setPointerCapture(gesture.id);
        emit('start');
      }
      if (gesture?.locked) {
        if (event.cancelable) event.preventDefault();
        emit('move', dx);
      }
    }
    function finish(event, x, y) {
      const dx = x - gesture.x;
      const dy = y - gesture.y;
      if (!gesture.locked && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        gesture.locked = true;
        emit('start');
      }
      const locked = gesture.locked;
      sample(x, event.timeStamp);
      const first = gesture.samples[0];
      const elapsed = event.timeStamp - first.time;
      const velocity = elapsed > 0 ? (x - first.x) / elapsed : 0;
      const flick = Math.abs(dx) >= 10 && Math.abs(velocity) >= 0.35 && dx * velocity > 0;
      const commit = Math.abs(dx) >= threshold || flick;
      reset();
      if (locked) {
        emit('move', dx);
        emit('end', dx, commit);
      }
    }
    surface.addEventListener('pointerdown', event => {
      if (directTouch && event.pointerType === 'touch') return;
      if (!event.isPrimary) { reset(true); return; }
      if (event.button !== 0 || isControl(event.target) || !canDrag()) return;
      begin(event.pointerId, event.clientX, event.clientY, event.timeStamp, 'pointer');
    }, options);
    surface.addEventListener('pointermove', event => {
      if (!gesture || gesture.kind !== 'pointer' || event.pointerId !== gesture.id) return;
      move(event, event.clientX, event.clientY);
    }, { ...options, passive: false });
    surface.addEventListener('pointerup', event => {
      if (!gesture || gesture.kind !== 'pointer' || event.pointerId !== gesture.id) return;
      finish(event, event.clientX, event.clientY);
    }, options);
    surface.addEventListener('pointercancel', event => {
      if (gesture?.kind === 'pointer' && event.pointerId === gesture.id) reset(true);
    }, options);
    surface.addEventListener('lostpointercapture', event => {
      if (event.target === surface && gesture?.kind === 'pointer' && event.pointerId === gesture.id) reset(true);
    }, options);
    surface.addEventListener('touchstart', event => {
      if (!directTouch) return;
      if (event.touches.length !== 1) { reset(true); return; }
      if (isControl(event.target) || !canDrag()) return;
      const touch = event.touches[0];
      begin(touch.identifier, touch.clientX, touch.clientY, event.timeStamp, 'touch');
    }, { ...options, passive: true });
    surface.addEventListener('touchmove', event => {
      if (!gesture || gesture.kind !== 'touch') return;
      if (event.touches.length !== 1) { reset(true); return; }
      const touch = Array.from(event.touches).find(item => item.identifier === gesture.id);
      if (touch) move(event, touch.clientX, touch.clientY);
    }, { ...options, passive: false });
    surface.addEventListener('touchend', event => {
      if (!gesture || gesture.kind !== 'touch') return;
      const touch = Array.from(event.changedTouches).find(item => item.identifier === gesture.id);
      if (touch) finish(event, touch.clientX, touch.clientY);
    }, options);
    surface.addEventListener('touchcancel', () => {
      if (gesture?.kind === 'touch') reset(true);
    }, options);
    surface.addEventListener('wheel', event => {
      if (event.ctrlKey || gesture || !canDrag() || isControl(event.target)) return;
      const dx = event.shiftKey && event.deltaX === 0 ? event.deltaY : event.deltaX;
      const dy = event.shiftKey && event.deltaX === 0 ? 0 : event.deltaY;
      if (Math.abs(dx) <= Math.abs(dy) * 1.2 || !dx || !event.cancelable) return;
      event.preventDefault();
      if (!wheel) {
        wheel = { offset: 0 };
        emit('start');
      }
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? surface.clientWidth : 1;
      const width = surface.clientWidth;
      wheel.offset = Math.max(-width, Math.min(width, wheel.offset - dx * unit));
      emit('move', wheel.offset);
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        if (!wheel) return;
        const offset = wheel.offset;
        wheel = undefined;
        emit('end', offset, Math.abs(offset) >= threshold);
      }, 160);
    }, { ...options, passive: false });
    surface.addEventListener('tudi:gallery-reset', () => reset(), options);
    surface.addEventListener('click', event => {
      if (Date.now() < suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
    }, { ...options, capture: true });
    surface.addEventListener('dragstart', event => event.preventDefault(), options);
    window.addEventListener('blur', () => reset(true), options);
    window.addEventListener('resize', () => {
      const width = surface.clientWidth;
      if (width === surfaceWidth) return;
      surfaceWidth = width;
      reset(true);
    }, options);
    window.addEventListener('pagehide', () => reset(true), options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) reset(true); }, options);
    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(surface)) { reset(true); events.abort(); }
    }, options);
  }
  const initialize = () => document.querySelectorAll('[data-swipe-gallery]').forEach(init);
  initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
